import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';

import {
  ClipboardCheck, Users, Eye, CheckCircle2,
  Clock, MapPin, AlertTriangle, Loader2, Building2,
  Calendar, Phone, Wrench, Search, Upload, Camera, Image, ChevronDown, ChevronUp, Trash2, ExternalLink, ClipboardList, ShieldAlert, ShieldCheck, X, Circle, User
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import api from '../../services/api';
import { useGoogleMaps, initializeMap, createMarker, createSearchBox, reverseGeocode } from '../../hooks/useGoogleMaps';
import { StageKey, StageName, SiteVisitStatus, POST_SV1_STAGE_KEYS, POST_SV2_STAGE_KEYS, DocumentTypeCode, DocumentReferenceType } from '../../constants/stageConstants';
import SupervisorCalendar from './SupervisorCalendar';

const SupervisorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const mapLoaded = useGoogleMaps();

  // Data states
  const [projects, setProjects] = useState([]);
  const [sv1MasterChecklist, setSv1MasterChecklist] = useState([]);
  const [sv2MasterChecklist, setSv2MasterChecklist] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [jobsProgress, setJobsProgress] = useState([]);

  // Work Progress (installation checklist) states
  const [wpSelectedProject, setWpSelectedProject] = useState(null);
  const [wpChecklist, setWpChecklist] = useState(null);
  const [wpLoading, setWpLoading] = useState(false);
  const [wpExpandedEntities, setWpExpandedEntities] = useState({});
  const [wpExpandedUnits, setWpExpandedUnits] = useState({});
  const [wpSearch, setWpSearch] = useState('');
  const [wpApprovingItem, setWpApprovingItem] = useState(null);
  const [wpPhotosOpen, setWpPhotosOpen] = useState(null); // responseId
  const [wpPhotoUrls, setWpPhotoUrls] = useState({}); // { responseId: [objectUrls] }
  const [wpLoadingPhotos, setWpLoadingPhotos] = useState(null);
  const [wpEnlargedPhoto, setWpEnlargedPhoto] = useState(null);

  // Site Visits tab states
  const [svFilter, setSvFilter] = useState('in-progress'); // 'in-progress' | 'completed'
  const [expandedSvProject, setExpandedSvProject] = useState(null); // project id
  const [svChecklist, setSvChecklist] = useState([]); // checklist for expanded project
  const [svSaving, setSvSaving] = useState(false);
  const [startingSv, setStartingSv] = useState(null); // `${projectId}-${visitNumber}` while starting

  // Client Review states
  const [crSelectedProject, setCrSelectedProject] = useState(null);
  const [crChecklist, setCrChecklist] = useState(null);
  const [crLoading, setCrLoading] = useState(false);
  const [crApprovingItem, setCrApprovingItem] = useState(null);
  const [crUpdatingItem, setCrUpdatingItem] = useState(null);
  const [crExpandedGroups, setCrExpandedGroups] = useState({});
  const [crItemPhotos, setCrItemPhotos] = useState({});
  const [crPhotosExpanded, setCrPhotosExpanded] = useState({});
  const [crEnlargedPhoto, setCrEnlargedPhoto] = useState(null);
  const [crCompleting, setCrCompleting] = useState(false);
  const [svPendingItems, setSvPendingItems] = useState('');
  const [svReworkDetails, setSvReworkDetails] = useState('');
  const [svInProgressProjects, setSvInProgressProjects] = useState([]); // lazy-loaded on tab click
  const [svInProgressLoaded, setSvInProgressLoaded] = useState(false);
  const [svInProgressLoading, setSvInProgressLoading] = useState(false);
  const [svCompletedProjects, setSvCompletedProjects] = useState([]); // lazy-loaded
  const [svCompletedLoaded, setSvCompletedLoaded] = useState(false);
  const [svCompletedLoading, setSvCompletedLoading] = useState(false);
  const [selectedVisitNumber, setSelectedVisitNumber] = useState(null); // 1 or 2 — for SV toggle

  // Site address editing states
  const [editingAddressProjectId, setEditingAddressProjectId] = useState(null);
  const [editingSiteAddress, setEditingSiteAddress] = useState('');
  const [editingLatitude, setEditingLatitude] = useState('');
  const [editingLongitude, setEditingLongitude] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchBoxRef = useRef(null);

  // Photo upload states
  const [uploadingPhotoItemId, setUploadingPhotoItemId] = useState(null);
  const [itemPhotos, setItemPhotos] = useState({}); // { responseId: [photoUrls] }
  const [viewingPhotosItemId, setViewingPhotosItemId] = useState(null);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null); // { url, fileName }

  // Dialog states
  const [addInstallerDialogOpen, setAddInstallerDialogOpen] = useState(false);
  const [creatingInstaller, setCreatingInstaller] = useState(false);
  const [jobDetailDialogOpen, setJobDetailDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  // Form states
  const [newInstaller, setNewInstaller] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    team: ''
  });


  useEffect(() => {
    fetchAllData();
  }, []);

  // Transform checklist from responses or fall back to master items
  const buildChecklist = (checklistResponses, masterItems) => {
    if (Array.isArray(checklistResponses) && checklistResponses.length > 0) {
      return checklistResponses.map(r => ({
        id: r.id || r.Id,
        checklistItemId: r.checklistItemId || r.ChecklistItemId,
        itemNumber: r.itemNumber || r.ItemNumber,
        item: r.checklistItemName || r.ChecklistItemName,
        groupName: r.groupName || r.GroupName || '',
        groupDisplayOrder: r.groupDisplayOrder || r.GroupDisplayOrder || 0,
        isRequired: r.isRequired || r.IsRequired || false,
        isConditional: r.isConditional || r.IsConditional || false,
        conditionNote: r.conditionNote || r.ConditionNote || null,
        isPhotoRequired: r.isPhotoRequired || r.IsPhotoRequired || false,
        completed: r.isCompleted || r.IsCompleted || false,
        notes: r.notes || r.Notes || '',
        remarks: r.remarks || r.Remarks || '',
      }));
    }
    return (masterItems || []).map(item => ({
      id: null,
      checklistItemId: item.Id || item.id,
      itemNumber: item.ItemNumber || item.itemNumber,
      item: item.ItemName || item.itemName,
      groupName: item.GroupName || item.groupName || '',
      groupDisplayOrder: item.GroupDisplayOrder || item.groupDisplayOrder || 0,
      isRequired: item.IsRequired || item.isRequired || false,
      isConditional: item.IsConditional || item.isConditional || false,
      conditionNote: item.ConditionNote || item.conditionNote || null,
      isPhotoRequired: item.IsPhotoRequired || item.isPhotoRequired || false,
      completed: false,
      notes: '',
      remarks: '',
    }));
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch projects + installers in parallel (independent endpoints)
      const [projectsResponse, installersRes] = await Promise.all([
        api.getSupervisorProjects(),
        api.getSupervisorInstallers().catch(() => [])
      ]);
      console.log('Supervisor Projects API Response:', projectsResponse);

      const allProjects = projectsResponse.Projects || projectsResponse.projects || projectsResponse.Items || projectsResponse.items || [];
      const sv1ChecklistMaster = projectsResponse.Sv1ChecklistItems || projectsResponse.sv1ChecklistItems || [];
      const sv2ChecklistMaster = projectsResponse.Sv2ChecklistItems || projectsResponse.sv2ChecklistItems || [];

      console.log('Projects Array:', allProjects);
      console.log('First Project Full Data:', allProjects[0]);
      console.log('SV1 Checklist Master:', sv1ChecklistMaster);
      console.log('SV2 Checklist Master:', sv2ChecklistMaster);

      // Store master checklist data
      setSv1MasterChecklist(sv1ChecklistMaster);
      setSv2MasterChecklist(sv2ChecklistMaster);

      // Transform project data
      const transformedProjects = allProjects.map(p => {
        const siteAddress = p.SiteAddress || p.siteAddress || 'N/A';

        const sv1Data = p.SiteVisitSV1 || p.siteVisitSV1;
        const sv2Data = p.SiteVisitSV2 || p.siteVisitSV2;

        return {
          id: p.Id || p.id,
          client_name: p.ClientName || p.clientName || p.client_name || 'N/A',
          location: siteAddress,
          latitude: p.Latitude || p.latitude || null,
          longitude: p.Longitude || p.longitude || null,
          sqft: p.Sqft || p.sqft || 0,
          status: p.StageName || p.stageName || p.stage_name || p.status || 'Unknown',
          stageKey: p.StageKey || p.stageKey || p.stage_key || '',
          supervisorId: p.SupervisorId || p.supervisorId || null,
          siteVisitSV1: sv1Data,
          siteVisitSV2: sv2Data,
        };
      });

      console.log('Transformed Projects:', transformedProjects);

      // Sort by SV1 scheduled date ascending (projects with date first, then without)
      transformedProjects.sort((a, b) => {
        const dateA = a.siteVisitSV1?.scheduledDate;
        const dateB = b.siteVisitSV1?.scheduledDate;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA) - new Date(dateB);
      });

      setProjects(transformedProjects);
      setJobsProgress([]);

      // Set installers from parallel fetch above
      setInstallers(Array.isArray(installersRes) ? installersRes : []);

      // Reset SV caches so they re-fetch when tab is clicked
      setSvInProgressLoaded(false);
      setSvInProgressProjects([]);
      setSvCompletedLoaded(false);
      setSvCompletedProjects([]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Work Progress: fetch installation checklist for a project ──
  // preserveExpansion=true keeps the user's current open entities/units when refreshing
  // (e.g., after approving a single item). When all currently-open entities/units have
  // no pending items left, we auto-open the next one that still has work.
  const fetchInstallationChecklist = async (projectId, { preserveExpansion = false } = {}) => {
    setWpLoading(true);
    if (!preserveExpansion) setWpChecklist(null);
    try {
      const data = await api.getSupervisorChecklist(projectId);
      setWpChecklist(data);

      if (!preserveExpansion) {
        // Initial open: collapse all, expand first entity + its first unit
        if (data.entities?.length > 0) {
          setWpExpandedEntities({ [data.entities[0].entityId]: true });
          if (data.entities[0].units?.length > 0) {
            setWpExpandedUnits({ [data.entities[0].units[0].unitId]: true });
          }
        }
      } else {
        // Refresh after approval: keep what the user already had open. If every
        // currently-open unit has no pending items, jump to the next pending one.
        const hasPending = (unit) => unit.groups?.some(g => g.items?.some(
          i => i.isCompleted && i.requiresSupervisorApproval && i.isSupervisorApproved !== true
        ));

        setWpExpandedEntities(prevEntities => {
          setWpExpandedUnits(prevUnits => {
            const openUnitsWithPending = (data.entities || []).some(e =>
              e.units?.some(u => prevUnits[u.unitId] && hasPending(u))
            );
            if (openUnitsWithPending) return prevUnits;

            // Find first pending unit anywhere and open it (+ its entity)
            for (const entity of data.entities || []) {
              for (const unit of entity.units || []) {
                if (hasPending(unit)) {
                  // ensure entity is open too
                  if (!prevEntities[entity.entityId]) {
                    setWpExpandedEntities(pe => ({ ...pe, [entity.entityId]: true }));
                  }
                  return { ...prevUnits, [unit.unitId]: true };
                }
              }
            }
            return prevUnits;
          });
          return prevEntities;
        });
      }
    } catch (error) {
      console.error('Error fetching installation checklist:', error);
      toast({ title: 'Error', description: 'Failed to load checklist', variant: 'destructive' });
    } finally {
      setWpLoading(false);
    }
  };

  const handleApproveItem = async (projectId, responseId) => {
    setWpApprovingItem(responseId);
    try {
      await api.supervisorApproveChecklist(projectId, responseId);
      toast({ title: 'Approved', description: 'Item approved successfully' });
      // Refresh checklist but keep the supervisor's place in the page
      await fetchInstallationChecklist(projectId, { preserveExpansion: true });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to approve', variant: 'destructive' });
    } finally {
      setWpApprovingItem(null);
    }
  };

  // ─── Client Review functions ───
  const fetchClientReviewChecklist = async (projectId) => {
    setCrLoading(true);
    setCrChecklist(null);
    try {
      const data = await api.getSupervisorClientReview(projectId);
      setCrChecklist(data);
      // Auto-expand all groups
      const expanded = {};
      (data.groups || []).forEach(g => { expanded[g.groupName] = true; });
      setCrExpandedGroups(expanded);
    } catch (error) {
      console.error('Error fetching client review checklist:', error);
      toast({ title: 'Error', description: 'Failed to load client review checklist', variant: 'destructive' });
    } finally {
      setCrLoading(false);
    }
  };

  const handleCrApproveItem = async (projectId, responseId) => {
    setCrApprovingItem(responseId);
    try {
      await api.supervisorApproveClientReview(projectId, responseId);
      toast({ title: 'Approved', description: 'Client review item approved' });
      await fetchClientReviewChecklist(projectId);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to approve', variant: 'destructive' });
    } finally {
      setCrApprovingItem(null);
    }
  };

  const handleCrSupervisorUpdate = async (projectId, responseId, currentCompleted) => {
    setCrUpdatingItem(responseId);
    try {
      await api.supervisorUpdateClientReview(projectId, responseId, !currentCompleted);
      await fetchClientReviewChecklist(projectId);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update', variant: 'destructive' });
    } finally {
      setCrUpdatingItem(null);
    }
  };

  const handleCrComplete = async (projectId) => {
    setCrCompleting(true);
    try {
      await api.supervisorCompleteClientReview(projectId);
      toast({ title: 'Success', description: 'Client review completed! Project moved to Pending OEM Signoff.' });
      setCrSelectedProject(null);
      setCrChecklist(null);
      fetchAllData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to complete client review', variant: 'destructive' });
    } finally {
      setCrCompleting(false);
    }
  };

  const loadCrItemPhotos = async (projectId, responseId) => {
    if (crItemPhotos[responseId]) {
      setCrPhotosExpanded(prev => ({ ...prev, [responseId]: !prev[responseId] }));
      return;
    }
    try {
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const docsRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents?referenceId=${responseId}`, { headers });
      if (!docsRes.ok) throw new Error('Failed to fetch documents');
      const data = await docsRes.json();
      const docsList = data.documents || data || [];

      const photos = [];
      for (const doc of docsList) {
        const streamRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${doc.id}/stream`, { headers });
        if (streamRes.ok) {
          const blob = await streamRes.blob();
          photos.push({ id: doc.id, url: URL.createObjectURL(blob), fileName: doc.fileName });
        }
      }
      setCrItemPhotos(prev => ({ ...prev, [responseId]: photos }));
      setCrPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
    } catch (error) {
      console.error('Error loading CR photos:', error);
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  const loadItemPhotos = async (projectId, responseId, photos) => {
    if (wpPhotoUrls[responseId]) {
      setWpPhotosOpen(wpPhotosOpen === responseId ? null : responseId);
      return;
    }
    setWpLoadingPhotos(responseId);
    try {
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const urls = await Promise.all(
        photos.map(async (p) => {
          try {
            const resp = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${p.documentId}/stream`, { headers });
            if (resp.ok) {
              const blob = await resp.blob();
              return { url: URL.createObjectURL(blob), fileName: p.fileName };
            }
          } catch { }
          return null;
        })
      );
      setWpPhotoUrls(prev => ({ ...prev, [responseId]: urls.filter(Boolean) }));
      setWpPhotosOpen(responseId);
    } catch {
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    } finally {
      setWpLoadingPhotos(null);
    }
  };

  // Lazy-load in-progress site visits when Site Visits tab is opened
  const fetchInProgressSiteVisits = async (force = false) => {
    if (svInProgressLoaded && !force) return;
    setSvInProgressLoading(true);
    try {
      const response = await api.getSvInProgress();
      const ipList = (response.Projects || response.projects || []).map(p => {
        const sv1Data = p.SiteVisitSV1 || p.siteVisitSV1;
        const sv2Data = p.SiteVisitSV2 || p.siteVisitSV2;
        return {
          id: p.Id || p.id,
          client_name: p.ClientName || p.clientName || 'N/A',
          location: p.SiteAddress || p.siteAddress || 'N/A',
          latitude: p.Latitude || p.latitude || null,
          longitude: p.Longitude || p.longitude || null,
          status: p.StageName || p.stageName || 'Unknown',
          stageKey: p.StageKey || p.stageKey || '',
          supervisorId: p.SupervisorId || p.supervisorId || null,
          siteVisitSV1: sv1Data,
          siteVisitSV2: sv2Data,
          site_visit_1_checklist: buildChecklist(
            sv1Data?.ChecklistResponses || sv1Data?.checklistResponses || [],
            sv1MasterChecklist
          ),
          site_visit_2_checklist: buildChecklist(
            sv2Data?.ChecklistResponses || sv2Data?.checklistResponses || [],
            sv2MasterChecklist
          ),
        };
      });
      setSvInProgressProjects(ipList);
      setSvInProgressLoaded(true);
    } catch (error) {
      console.error('Error fetching in-progress site visits:', error);
      toast({ title: 'Error', description: 'Failed to load in-progress site visits', variant: 'destructive' });
    } finally {
      setSvInProgressLoading(false);
    }
  };

  // Lazy-load completed site visits only when tab is switched
  const fetchCompletedSiteVisits = async (force = false) => {
    if (svCompletedLoaded && !force) return;
    setSvCompletedLoading(true);
    try {
      const response = await api.getSvCompleted();
      const completedList = (response.Projects || response.projects || []).map(p => {
        const sv1Data = p.SiteVisitSV1 || p.siteVisitSV1;
        const sv2Data = p.SiteVisitSV2 || p.siteVisitSV2;
        return {
          id: p.Id || p.id,
          client_name: p.ClientName || p.clientName || 'N/A',
          location: p.SiteAddress || p.siteAddress || 'N/A',
          latitude: p.Latitude || p.latitude || null,
          longitude: p.Longitude || p.longitude || null,
          status: p.StageName || p.stageName || 'Unknown',
          stageKey: p.StageKey || p.stageKey || '',
          supervisorId: p.SupervisorId || p.supervisorId || null,
          siteVisitSV1: sv1Data,
          siteVisitSV2: sv2Data,
          site_visit_1_checklist: buildChecklist(
            sv1Data?.ChecklistResponses || sv1Data?.checklistResponses || [],
            sv1MasterChecklist
          ),
          site_visit_2_checklist: buildChecklist(
            sv2Data?.ChecklistResponses || sv2Data?.checklistResponses || [],
            sv2MasterChecklist
          ),
        };
      });
      setSvCompletedProjects(completedList);
      setSvCompletedLoaded(true);
    } catch (error) {
      console.error('Error fetching completed site visits:', error);
      toast({ title: 'Error', description: 'Failed to load completed site visits', variant: 'destructive' });
    } finally {
      setSvCompletedLoading(false);
    }
  };

  // Start SV: just call API, show toast, switch to Site Visits tab
  const handleStartSiteVisit = async (project, visitNumber) => {
    const key = `${project.id}-${visitNumber}`;
    if (startingSv) return; // prevent double click
    setStartingSv(key);
    try {
      await api.startSiteVisit(project.id, visitNumber);
      toast({
        title: `SV${visitNumber} Started`,
        description: `Site Visit ${visitNumber} for ${project.client_name} is now in progress`
      });
      await fetchAllData();
      setActiveTab('sitevisits');
      // Force re-fetch since fetchAllData reset the cache
      fetchInProgressSiteVisits(true);
    } catch (error) {
      console.error('Error starting site visit:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start site visit',
        variant: 'destructive'
      });
    } finally {
      setStartingSv(null);
    }
  };

  // Get site visit projects from dedicated API data
  const getSvProjects = (filter) => {
    if (filter === 'in-progress') return svInProgressProjects;
    if (filter === 'completed') return svCompletedProjects;
    return [];
  };

  // Expand a project in Site Visits tab to show its checklist
  const handleExpandSvProject = (project, forceVisitNumber = null) => {
    if (expandedSvProject === project.id && !forceVisitNumber) {
      return; // Already selected, keep it
    }
    setExpandedSvProject(project.id);
    // Determine which visit to show
    const sv1InProgress = project.siteVisitSV1?.status === SiteVisitStatus.InProgress;
    const sv2InProgress = project.siteVisitSV2?.status === SiteVisitStatus.InProgress;
    const sv1Completed = project.siteVisitSV1?.status === SiteVisitStatus.Completed;
    const sv2Completed = project.siteVisitSV2?.status === SiteVisitStatus.Completed;
    // Check which SVs are assigned to the current user
    const sv1Mine = (project.siteVisitSV1?.AssignedSupervisorId || project.siteVisitSV1?.assignedSupervisorId) === user?.id;
    const sv2Mine = (project.siteVisitSV2?.AssignedSupervisorId || project.siteVisitSV2?.assignedSupervisorId) === user?.id;
    // Default to the SV assigned to the current user
    const visitNumber = forceVisitNumber || (
      svFilter === 'completed'
        ? (sv1Completed && sv1Mine ? 1 : sv2Completed && sv2Mine ? 2 : sv1Completed ? 1 : 2)
        : (sv1InProgress && sv1Mine ? 1 : sv2InProgress && sv2Mine ? 2 : sv1Mine ? 1 : sv2Mine ? 2 : 1)
    );
    setSelectedVisitNumber(visitNumber);
    const checklist = visitNumber === 1 ? project.site_visit_1_checklist : project.site_visit_2_checklist;
    setSvChecklist(checklist || []);
    setSvPendingItems('');
    setSvReworkDetails('');
    // Load photos for each checklist item
    loadPhotosForProject(project.id, visitNumber);
  };

  // Switch between SV1 and SV2 for the currently expanded project
  const handleSwitchVisit = (visitNumber) => {
    const project = getSvProjects(svFilter).find(p => p.id === expandedSvProject);
    if (project) {
      handleExpandSvProject(project, visitNumber);
    }
  };

  // Load photos for all checklist items
  const loadPhotosForProject = async (projectId, visitNumber) => {
    try {
      const typeCode = visitNumber === 1 ? DocumentTypeCode.Sv1Photo : DocumentTypeCode.Sv2Photo;
      const docs = await api.request(`/api/projects/${projectId}/documents?documentTypeCode=${typeCode}`);
      const docsList = docs.documents || docs.Documents || docs || [];
      const photoMap = {};
      const token = localStorage.getItem('alignworks_token');

      // Fetch each photo as blob with auth and create object URLs
      await Promise.all(docsList.map(async (doc) => {
        const refId = doc.referenceId || doc.ReferenceId;
        const docId = doc.id || doc.Id;
        if (!refId || !docId) return;

        let blobUrl = '';
        try {
          const res = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${docId}/stream`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (res.ok) {
            const blob = await res.blob();
            blobUrl = URL.createObjectURL(blob);
          }
        } catch (e) {
          console.error('Error fetching photo blob:', e);
        }

        if (!photoMap[refId]) photoMap[refId] = [];
        photoMap[refId].push({
          id: docId,
          url: blobUrl,
          fileName: doc.fileName || doc.FileName
        });
      }));

      setItemPhotos(photoMap);
    } catch (error) {
      console.error('Error loading photos:', error);
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  // Upload photo for a checklist item
  const handlePhotoUpload = async (projectId, visitNumber, responseId, file) => {
    if (!file) return;
    setUploadingPhotoItemId(responseId);
    try {
      const typeCode = visitNumber === 1 ? DocumentTypeCode.Sv1Photo : DocumentTypeCode.Sv2Photo;
      const refType = visitNumber === 1 ? DocumentReferenceType.Sv1Response : DocumentReferenceType.Sv2Response;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentTypeCode', typeCode);
      formData.append('subfolder', `sv${visitNumber}`);
      formData.append('referenceType', refType);
      formData.append('referenceId', responseId);

      const token = localStorage.getItem('alignworks_token');
      const response = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      toast({ title: 'Photo Uploaded', description: 'Photo attached to checklist item' });
      loadPhotosForProject(projectId, visitNumber);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({ title: 'Error', description: 'Failed to upload photo', variant: 'destructive' });
    } finally {
      setUploadingPhotoItemId(null);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (projectId, visitNumber, documentId) => {
    try {
      await api.request(`/api/projects/${projectId}/documents/${documentId}`, { method: 'DELETE' });
      toast({ title: 'Photo Deleted', description: 'Photo removed successfully' });
      loadPhotosForProject(projectId, visitNumber);
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast({ title: 'Error', description: 'Failed to delete photo', variant: 'destructive' });
    }
  };

  // SV checklist toggle/notes/remarks
  const handleSvChecklistToggle = (itemId) => {
    setSvChecklist(prev => prev.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    ));
  };
  const handleSvChecklistNotes = (itemId, notes) => {
    setSvChecklist(prev => prev.map(item =>
      item.id === itemId ? { ...item, notes } : item
    ));
  };
  const handleSvChecklistRemarks = (itemId, remarks) => {
    setSvChecklist(prev => prev.map(item =>
      item.id === itemId ? { ...item, remarks } : item
    ));
  };

  // Save SV progress
  const handleSaveSvProgress = async (project) => {
    setSvSaving(true);
    try {
      const visitNumber = selectedVisitNumber;

      const checklistPayload = svChecklist
        .filter(item => item.id)
        .map(item => ({
          id: item.id,
          isCompleted: item.completed,
          notes: item.notes || null,
          remarks: item.remarks || null
        }));

      await api.updateSiteVisit(project.id, visitNumber, checklistPayload,
        svPendingItems ? svPendingItems.split('\n').filter(i => i.trim()) : null,
        svReworkDetails || null
      );

      toast({ title: 'Progress Saved', description: 'Checklist progress saved successfully' });
      // Re-fetch SV data to reflect saved state
      await fetchInProgressSiteVisits(true);
    } catch (error) {
      console.error('Error saving progress:', error);
      toast({ title: 'Error', description: 'Failed to save progress', variant: 'destructive' });
    } finally {
      setSvSaving(false);
    }
  };

  // Complete SV
  const handleCompleteSv = async (project) => {
    const incompleteRequired = svChecklist.filter(item => item.isRequired && !item.completed);
    if (incompleteRequired.length > 0) {
      toast({
        title: 'Incomplete Required Items',
        description: `Complete all required items (${incompleteRequired.length} remaining)`,
        variant: 'destructive'
      });
      return;
    }

    const missingPhotos = svChecklist.filter(item => item.isPhotoRequired && !(itemPhotos[item.id]?.length));
    if (missingPhotos.length > 0) {
      toast({
        title: 'Photos Required',
        description: `${missingPhotos.length} checklist item(s) require photos but none uploaded`,
        variant: 'destructive'
      });
      return;
    }

    setSvSaving(true);
    try {
      const visitNumber = selectedVisitNumber;

      // Save latest state first
      const checklistPayload = svChecklist.filter(item => item.id).map(item => ({
        id: item.id, isCompleted: item.completed, notes: item.notes || null, remarks: item.remarks || null
      }));
      await api.updateSiteVisit(project.id, visitNumber, checklistPayload, null, null);
      await api.completeSiteVisit(project.id, visitNumber);

      toast({ title: 'Site Visit Completed', description: `SV${visitNumber} marked as complete` });
      setExpandedSvProject(null);
      // Refresh both Projects tab and SV tabs
      await fetchAllData();
      fetchInProgressSiteVisits(true);
      fetchCompletedSiteVisits(true);
    } catch (error) {
      console.error('Error completing SV:', error);
      toast({ title: 'Error', description: error.message || 'Failed to complete site visit', variant: 'destructive' });
    } finally {
      setSvSaving(false);
    }
  };

  // Site address editing with Google Maps
  const handleEditAddress = (project) => {
    setEditingAddressProjectId(project.id);
    setEditingSiteAddress(project.location !== 'N/A' ? project.location : '');
    setEditingLatitude('');
    setEditingLongitude('');
    // Map will be initialized after dialog mounts
    setTimeout(() => setupAddressMap(project), 500);
  };

  const setupAddressMap = (project) => {
    const mapElement = document.getElementById(`sv-map-${project.id}`);
    if (!mapElement || !mapLoaded) return;

    const hasCoords = project.latitude && project.longitude;
    const center = hasCoords
      ? { lat: parseFloat(project.latitude), lng: parseFloat(project.longitude) }
      : null;

    const newMap = initializeMap(`sv-map-${project.id}`, {
      zoom: hasCoords ? 15 : 12,
      ...(center && { center })
    });
    if (!newMap) return;
    mapRef.current = newMap;

    // If project has coordinates, place a marker there
    if (hasCoords) {
      const latLng = new window.google.maps.LatLng(center.lat, center.lng);
      updateAddressMarker(newMap, latLng);
    } else if (project.location && project.location !== 'N/A') {
      // No coordinates — geocode the address to center the map
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: project.location }, (results, status) => {
        if (status === 'OK' && results[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          newMap.setCenter(loc);
          newMap.setZoom(15);
          updateAddressMarker(newMap, loc);
        }
      });
    }

    const input = document.getElementById(`sv-search-${project.id}`);
    if (input) {
      const sb = createSearchBox(input, newMap);
      if (sb) {
        sb.addListener('places_changed', () => {
          const places = sb.getPlaces();
          if (!places || places.length === 0) return;
          const place = places[0];
          if (!place.geometry?.location) return;
          newMap.setCenter(place.geometry.location);
          newMap.setZoom(15);
          updateAddressMarker(newMap, place.geometry.location, place.formatted_address);
        });
        searchBoxRef.current = sb;
      }
    }

    newMap.addListener('click', async (e) => {
      updateAddressMarker(newMap, e.latLng);
      try {
        const address = await reverseGeocode(e.latLng);
        setEditingSiteAddress(address);
      } catch (err) { console.error(err); }
    });
  };

  const updateAddressMarker = (mapInstance, location, address = null) => {
    if (markerRef.current) markerRef.current.setMap(null);
    const newMarker = createMarker(mapInstance, location);
    setEditingLatitude(location.lat().toFixed(6));
    setEditingLongitude(location.lng().toFixed(6));
    if (address) setEditingSiteAddress(address);

    newMarker.addListener('dragend', async (e) => {
      setEditingLatitude(e.latLng.lat().toFixed(6));
      setEditingLongitude(e.latLng.lng().toFixed(6));
      try {
        const addr = await reverseGeocode(e.latLng);
        setEditingSiteAddress(addr);
      } catch (err) { console.error(err); }
    });
    markerRef.current = newMarker;
  };

  const handleSaveAddress = async (projectId) => {
    setSavingAddress(true);
    try {
      await api.request(`/api/supervisor/update-address/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify({
          siteAddress: editingSiteAddress,
          latitude: editingLatitude ? parseFloat(editingLatitude) : null,
          longitude: editingLongitude ? parseFloat(editingLongitude) : null
        }),
      });
      toast({ title: 'Address Updated', description: 'Site address saved successfully' });
      setEditingAddressProjectId(null);
      // Update address locally instead of full refetch to avoid clearing tab state
      setProjects(prev => prev.map(p => p.id === projectId ? {
        ...p,
        location: editingSiteAddress,
        latitude: editingLatitude ? parseFloat(editingLatitude) : p.latitude,
        longitude: editingLongitude ? parseFloat(editingLongitude) : p.longitude,
      } : p));
      setSvInProgressProjects(prev => prev.map(p => p.id === projectId ? {
        ...p,
        location: editingSiteAddress,
        latitude: editingLatitude ? parseFloat(editingLatitude) : p.latitude,
        longitude: editingLongitude ? parseFloat(editingLongitude) : p.longitude,
      } : p));
    } catch (error) {
      console.error('Error updating address:', error);
      toast({ title: 'Error', description: 'Failed to update address', variant: 'destructive' });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleGetLiveLocation = () => {
    if (!mapRef.current || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      mapRef.current.setCenter(loc);
      mapRef.current.setZoom(15);
      const latLng = new window.google.maps.LatLng(loc.lat, loc.lng);
      updateAddressMarker(mapRef.current, latLng);
      try {
        const address = await reverseGeocode(latLng);
        setEditingSiteAddress(address);
      } catch (err) { console.error(err); }
    }, (err) => {
      toast({ title: 'Location Error', description: 'Could not get your location', variant: 'destructive' });
    });
  };

  const handleCreateInstaller = async () => {
    setCreatingInstaller(true);
    try {
      await api.createInstallerBySupervisor({
        ...newInstaller,
        role: 'installer'
      });

      toast({
        title: 'Aligner Created',
        description: `${newInstaller.name} has been added as an aligner`
      });

      setAddInstallerDialogOpen(false);
      setNewInstaller({ name: '', email: '', password: '', phone: '', team: '' });
      fetchAllData();
    } catch (error) {
      console.error('Error creating aligner:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create aligner',
        variant: 'destructive'
      });
    } finally {
      setCreatingInstaller(false);
    }
  };

  const handleApproveCompletion = async (jobId, approved) => {
    try {
      await api.supervisorApproveCompletion(jobId, approved, null);

      toast({
        title: approved ? 'Job Approved' : 'Job Rejected',
        description: approved ? 'Job completion has been approved' : 'Job completion has been rejected'
      });

      setJobDetailDialogOpen(false);
      fetchAllData();
    } catch (error) {
      console.error('Error approving completion:', error);
      toast({
        title: 'Error',
        description: 'Failed to update job status',
        variant: 'destructive'
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      // Initial stages
      [StageName.NewProject]: 'bg-slate-100 text-slate-700 border-slate-300',
      [StageName.QuotePending]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Quote Sent': 'bg-yellow-100 text-yellow-700 border-yellow-300',

      // Site Visit 1
      [StageName.Sv1Pending]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv1Scheduled]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv1Completed]: 'bg-sky-100 text-sky-700 border-sky-300',
      'SV1 Complete': 'bg-sky-100 text-sky-700 border-sky-300',

      // Site Visit 2
      [StageName.Sv2Proposed]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv2Pending]: 'bg-orange-100 text-orange-700 border-orange-300',
      [StageName.Sv2Scheduled]: 'bg-orange-100 text-orange-700 border-orange-300',
      [StageName.Sv2Completed]: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'SV2 Complete': 'bg-indigo-100 text-indigo-700 border-indigo-300',

      // Installation stages
      [StageName.InstallationScheduled]: 'bg-blue-100 text-blue-700 border-blue-300',
      [StageName.Scheduled]: 'bg-blue-100 text-blue-700 border-blue-300',
      [StageName.InstallationInProgress]: 'bg-purple-100 text-purple-700 border-purple-300',
      [StageName.InProgress]: 'bg-purple-100 text-purple-700 border-purple-300',
      [StageName.InstallationCompleted]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.ClientReview]: 'bg-cyan-100 text-cyan-700 border-cyan-300',

      // Completion stages
      [StageName.Completed]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.Delivered]: 'bg-emerald-100 text-emerald-700 border-emerald-300',

      // Other statuses
      [StageName.OnHold]: 'bg-red-100 text-red-700 border-red-300',
      [StageName.Cancelled]: 'bg-gray-100 text-gray-700 border-gray-300',
      [StageName.PendingApproval]: 'bg-pink-100 text-pink-700 border-pink-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Stats calculations - only count SVs assigned to the current supervisor
  const sv1PendingCount = projects.filter(p => {
    const sv1 = p.siteVisitSV1;
    const assignedTo = sv1?.AssignedSupervisorId || sv1?.assignedSupervisorId;
    return assignedTo === user?.id && (
      p.status === StageName.Sv1Scheduled ||
      sv1?.status === SiteVisitStatus.Scheduled ||
      sv1?.status === SiteVisitStatus.InProgress
    );
  }).length;
  const sv2PendingCount = projects.filter(p => {
    const sv2 = p.siteVisitSV2;
    const assignedTo = sv2?.AssignedSupervisorId || sv2?.assignedSupervisorId;
    return assignedTo === user?.id && (
      p.status === StageName.Sv2Scheduled ||
      sv2?.status === SiteVisitStatus.Scheduled ||
      sv2?.status === SiteVisitStatus.InProgress
    );
  }).length;
  const activeInstallersCount = installers.length;
  const pendingApprovals = jobsProgress.filter(j => j.status === 'In Progress' && j.progress_percent === 100).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="supervisor-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">SuperAligner Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back, {user?.name}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/employee/profile')}
            className="border-gray-300 self-start sm:self-auto"
          >
            <User className="w-4 h-4 mr-1" />
            Profile
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-yellow-600">SV1 Pending</p>
                  <p className="text-2xl sm:text-3xl font-bold text-yellow-700">{sv1PendingCount}</p>
                </div>
                <ClipboardCheck className="w-6 h-6 sm:w-10 sm:h-10 text-yellow-500 opacity-80 shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-orange-600">SV2 Pending</p>
                  <p className="text-2xl sm:text-3xl font-bold text-orange-700">{sv2PendingCount}</p>
                </div>
                <Calendar className="w-6 h-6 sm:w-10 sm:h-10 text-orange-500 opacity-80 shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 col-span-2 md:col-span-1">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-blue-600">Active Aligners</p>
                  <p className="text-2xl sm:text-3xl font-bold text-blue-700">{activeInstallersCount}</p>
                </div>
                <Users className="w-6 h-6 sm:w-10 sm:h-10 text-blue-500 opacity-80 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-full sm:w-auto flex overflow-x-auto justify-start">
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="inline">My </span>Projects
            </TabsTrigger>
            <TabsTrigger value="sitevisits" className="flex items-center gap-2" onClick={() => fetchInProgressSiteVisits()}>
              <ClipboardCheck className="w-4 h-4" />
              <span className="inline">Site </span>Visits
            </TabsTrigger>
            <TabsTrigger value="progress" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              <span className="inline">Work </span>Progress
            </TabsTrigger>
            <TabsTrigger value="clientreview" className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" />
              <span className="inline">Client </span>Review
            </TabsTrigger>
            <TabsTrigger value="installers" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Aligners
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Schedule
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <CardTitle>Assigned Projects</CardTitle>
                <CardDescription>Manage site visits and track project progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto -mx-6 px-6">
                  <Table className="min-w-[1000px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Client</TableHead>
                        <TableHead className="whitespace-nowrap">Location</TableHead>
                        <TableHead className="whitespace-nowrap">Sq.Ft</TableHead>
                        <TableHead className="whitespace-nowrap">SV1 Scheduled</TableHead>
                        <TableHead className="whitespace-nowrap">Status</TableHead>
                        <TableHead className="whitespace-nowrap">Site Visit 1</TableHead>
                        <TableHead className="whitespace-nowrap">Site Visit 2</TableHead>
                        <TableHead className="whitespace-nowrap">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projects.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                            No projects assigned yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        projects.map(project => (
                          <TableRow key={project.id}>
                            <TableCell className="font-medium">{project.client_name}</TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="flex items-center gap-1 text-gray-600 min-w-0" title={project.location}>
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{project.location}</span>
                              </div>
                            </TableCell>
                            <TableCell>{project.sqft}</TableCell>
                            <TableCell>
                              {project.siteVisitSV1?.scheduledDate && !project.siteVisitSV1.scheduledDate.startsWith('0001') ? (
                                <div className="text-sm">
                                  <div className="font-medium">{format(new Date(project.siteVisitSV1.scheduledDate.includes('T') ? project.siteVisitSV1.scheduledDate : project.siteVisitSV1.scheduledDate + 'T00:00:00'), 'MMM dd, yyyy')}</div>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                                {project.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              {POST_SV1_STAGE_KEYS.includes(project.stageKey)
                                || project.siteVisitSV1?.status === SiteVisitStatus.Completed ? (
                                <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {project.siteVisitSV1?.completedAt
                                    ? format(new Date(project.siteVisitSV1.completedAt), 'MMM dd, yyyy')
                                    : 'Completed'}
                                </span>
                              ) : project.siteVisitSV1?.status === SiteVisitStatus.InProgress ? (
                                <span className="flex items-center gap-1 text-blue-600">
                                  <Clock className="w-4 h-4" />
                                  In Progress
                                </span>
                              ) : (project.stageKey === StageKey.Sv1Scheduled || project.status === StageName.Sv1Scheduled) ? (
                                <span className="flex items-center gap-1 text-yellow-600">
                                  <Clock className="w-4 h-4" />
                                  Scheduled
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {POST_SV2_STAGE_KEYS.includes(project.stageKey)
                                || project.siteVisitSV2?.status === SiteVisitStatus.Completed ? (
                                <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {project.siteVisitSV2?.completedAt
                                    ? format(new Date(project.siteVisitSV2.completedAt), 'MMM dd, yyyy')
                                    : 'Completed'}
                                </span>
                              ) : project.siteVisitSV2?.status === SiteVisitStatus.InProgress ? (
                                <span className="flex items-center gap-1 text-blue-600">
                                  <Clock className="w-4 h-4" />
                                  In Progress
                                </span>
                              ) : (project.stageKey === StageKey.Sv2Scheduled || project.status === StageName.Sv2Scheduled) ? (
                                <span className="flex items-center gap-1 text-orange-600">
                                  <Clock className="w-4 h-4" />
                                  Scheduled
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 flex-wrap">
                                {project.supervisorId === user?.id && (
                                  <>
                                    {(project.status === StageName.Sv1Scheduled || project.siteVisitSV1?.status === SiteVisitStatus.Scheduled) && project.siteVisitSV1?.status !== SiteVisitStatus.InProgress && (
                                      <Button
                                        size="sm"
                                        onClick={() => handleStartSiteVisit(project, 1)}
                                        disabled={startingSv !== null}
                                        data-testid={`sv1-btn-${project.id}`}
                                      >
                                        {startingSv === `${project.id}-1` ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                            Starting...
                                          </>
                                        ) : (
                                          'Start SV1'
                                        )}
                                      </Button>
                                    )}

                                    {(project.status === StageName.Sv2Scheduled || project.siteVisitSV2?.status === SiteVisitStatus.Scheduled) && project.siteVisitSV2?.status !== SiteVisitStatus.InProgress && (
                                      <Button
                                        size="sm"
                                        onClick={() => handleStartSiteVisit(project, 2)}
                                        disabled={startingSv !== null}
                                        data-testid={`sv2-btn-${project.id}`}
                                      >
                                        {startingSv === `${project.id}-2` ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                            Starting...
                                          </>
                                        ) : (
                                          'Start SV2'
                                        )}
                                      </Button>
                                    )}

                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/projects/${project.id}/view`)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Visits Tab */}
          <TabsContent value="sitevisits">
            {/* Filter toggle */}
            <div className="flex gap-3 mb-4">
              <Button
                variant={svFilter === 'in-progress' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setSvFilter('in-progress'); setExpandedSvProject(null); setSvChecklist([]); }}
              >
                <Clock className="w-4 h-4 mr-1" />
                In Progress ({getSvProjects('in-progress').length})
              </Button>
              <Button
                variant={svFilter === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setSvFilter('completed'); setExpandedSvProject(null); setSvChecklist([]); fetchCompletedSiteVisits(); }}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Completed {svCompletedLoaded ? `(${svCompletedProjects.length})` : ''}
              </Button>
            </div>

            {/* Master-Detail Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* LEFT PANEL - Project List */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    {svFilter === 'in-progress' ? 'In Progress' : 'Completed'} ({getSvProjects(svFilter).length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {(svFilter === 'in-progress' && svInProgressLoading) || (svFilter === 'completed' && svCompletedLoading) ? (
                      <div className="text-center py-8 text-gray-500">
                        <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin opacity-50" />
                        <p>Loading {svFilter === 'in-progress' ? 'in-progress' : 'completed'} site visits...</p>
                      </div>
                    ) : getSvProjects(svFilter).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <ClipboardCheck className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>No {svFilter === 'in-progress' ? 'in progress' : 'completed'} site visits</p>
                      </div>
                    ) : (
                      getSvProjects(svFilter).map(project => {
                        const sv1IP = project.siteVisitSV1?.status === SiteVisitStatus.InProgress;
                        const sv2IP = project.siteVisitSV2?.status === SiteVisitStatus.InProgress;
                        const sv1Done = project.siteVisitSV1?.status === SiteVisitStatus.Completed;
                        const sv2Done = project.siteVisitSV2?.status === SiteVisitStatus.Completed;
                        // In completed tab, prioritize showing the completed visit
                        const vn = svFilter === 'completed'
                          ? (sv1Done ? 1 : sv2Done ? 2 : 1)
                          : (sv1IP ? 1 : sv2IP ? 2 : sv1Done ? 1 : 2);
                        const cl = vn === 1 ? project.site_visit_1_checklist : project.site_visit_2_checklist;
                        const done = cl.filter(i => i.completed).length;
                        const total = cl.length;
                        const pct = total > 0 ? Math.round((done / total) * 100) : 0;

                        return (
                          <div
                            key={project.id}
                            onClick={() => handleExpandSvProject(project)}
                            className={`p-3 rounded-lg cursor-pointer transition-all ${expandedSvProject === project.id
                              ? 'bg-blue-50 border-2 border-blue-500'
                              : 'bg-white border border-gray-200 hover:border-blue-300'
                              }`}
                          >
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-gray-900 truncate">{project.client_name}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${vn === 1
                                ? (sv1IP ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700')
                                : (sv2IP ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700')
                                }`}>
                                SV{vn}
                              </span>
                            </div>
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                <span>{done}/{total} items</span>
                                <span>{pct}%</span>
                              </div>
                              <Progress value={pct} className="h-1.5" />
                              {svFilter === 'completed' && (() => {
                                const completedAt = vn === 1
                                  ? (project.siteVisitSV1?.CompletedAt || project.siteVisitSV1?.completedAt)
                                  : (project.siteVisitSV2?.CompletedAt || project.siteVisitSV2?.completedAt);
                                return completedAt ? (
                                  <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {format(new Date(completedAt), 'MMM dd, yyyy')}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RIGHT PANEL - Selected Project Details */}
              <Card className="lg:col-span-2">
                {expandedSvProject ? (() => {
                  const project = getSvProjects(svFilter).find(p => p.id === expandedSvProject);
                  if (!project) return null;
                  const sv1InProgress = project.siteVisitSV1?.status === SiteVisitStatus.InProgress;
                  const sv2InProgress = project.siteVisitSV2?.status === SiteVisitStatus.InProgress;
                  const sv1Completed = project.siteVisitSV1?.status === SiteVisitStatus.Completed;
                  const hasSV1 = !!project.siteVisitSV1;
                  const hasSV2 = !!project.siteVisitSV2;
                  const sv1AssignedToMe = hasSV1 && (project.siteVisitSV1?.AssignedSupervisorId || project.siteVisitSV1?.assignedSupervisorId) === user?.id;
                  const sv2AssignedToMe = hasSV2 && (project.siteVisitSV2?.AssignedSupervisorId || project.siteVisitSV2?.assignedSupervisorId) === user?.id;
                  const hasBothVisitsForMe = sv1AssignedToMe && sv2AssignedToMe;
                  const sv2Completed = project.siteVisitSV2?.status === SiteVisitStatus.Completed;
                  const currentVisitCompleted = selectedVisitNumber === 1 ? sv1Completed : sv2Completed;
                  const isEditable = svFilter !== 'completed' && !currentVisitCompleted;

                  return (
                    <>
                      <CardHeader className="pb-3">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-xl">{project.client_name}</CardTitle>
                            <p className="text-sm text-gray-500 flex items-start gap-1 mt-1">
                              <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              <span className="break-words">{project.location}</span>
                            </p>
                          </div>
                          <div className="flex flex-row sm:flex-col flex-wrap items-start sm:items-end gap-2 sm:flex-shrink-0">
                            {hasBothVisitsForMe ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant={selectedVisitNumber === 1 ? 'default' : 'outline'}
                                  className="h-7 text-xs px-3"
                                  onClick={() => handleSwitchVisit(1)}
                                >
                                  SV1 {project.siteVisitSV1?.status === SiteVisitStatus.InProgress ? '(In Progress)' : project.siteVisitSV1?.status === SiteVisitStatus.Completed ? '(Completed)' : ''}
                                </Button>
                                <Button
                                  size="sm"
                                  variant={selectedVisitNumber === 2 ? 'default' : 'outline'}
                                  className="h-7 text-xs px-3"
                                  onClick={() => handleSwitchVisit(2)}
                                >
                                  SV2 {project.siteVisitSV2?.status === SiteVisitStatus.InProgress ? '(In Progress)' : project.siteVisitSV2?.status === SiteVisitStatus.Completed ? '(Completed)' : ''}
                                </Button>
                              </div>
                            ) : (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${sv1InProgress || sv2InProgress ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                SV{selectedVisitNumber} {sv1InProgress || sv2InProgress ? 'In Progress' : 'Completed'}
                              </span>
                            )}
                            <div className="flex items-center gap-1">
                              {project.location && project.location !== 'N/A' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    const query = project.latitude && project.longitude
                                      ? `${project.latitude},${project.longitude}`
                                      : encodeURIComponent(project.location);
                                    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
                                  }}
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  View Location
                                </Button>
                              )}
                              {isEditable && !((sv1Completed || POST_SV1_STAGE_KEYS.includes(project.stageKey)) && project.latitude && project.longitude) && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleEditAddress(project)}>
                                  <MapPin className="w-3 h-3 mr-1" />
                                  Update Location
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
                        {/* Progress summary */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {svChecklist.filter(i => i.completed).length}/{svChecklist.length} completed
                          </span>
                          {svChecklist.some(i => i.isRequired && !i.completed) && (
                            <span className="text-red-500 text-xs">
                              {svChecklist.filter(i => i.isRequired && !i.completed).length} required left
                            </span>
                          )}
                        </div>
                        <Progress
                          value={svChecklist.length > 0 ? Math.round((svChecklist.filter(i => i.completed).length / svChecklist.length) * 100) : 0}
                          className="h-2"
                        />
                        {/* Completed on date */}
                        {(() => {
                          const completedAt = selectedVisitNumber === 1
                            ? (project.siteVisitSV1?.CompletedAt || project.siteVisitSV1?.completedAt)
                            : (project.siteVisitSV2?.CompletedAt || project.siteVisitSV2?.completedAt);
                          return completedAt ? (
                            <div className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                              <CheckCircle2 className="w-4 h-4" />
                              Completed on {format(new Date(completedAt), 'MMM dd, yyyy')}
                            </div>
                          ) : null;
                        })()}

                        {/* Grouped checklist items */}
                        {(() => {
                          const groups = {};
                          svChecklist.forEach(item => {
                            const group = item.groupName || 'General';
                            if (!groups[group]) groups[group] = { order: item.groupDisplayOrder || 0, items: [] };
                            groups[group].items.push(item);
                          });
                          const sortedGroups = Object.entries(groups).sort((a, b) => a[1].order - b[1].order);

                          return sortedGroups.map(([groupName, groupData]) => (
                            <div key={groupName} className="space-y-2">
                              <h5 className="font-semibold text-xs text-gray-600 bg-gray-100 px-3 py-1.5 rounded">
                                {groupName}
                                <span className="ml-2 text-muted-foreground">
                                  ({groupData.items.filter(i => i.completed).length}/{groupData.items.length})
                                </span>
                              </h5>
                              <div className="space-y-2">
                                {groupData.items.map(item => (
                                  <div key={item.id || item.checklistItemId} className="p-3 border rounded-lg space-y-2">
                                    {/* Completed read-only view */}
                                    {!isEditable ? (
                                      <>
                                        <div className="flex items-start gap-3">
                                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.completed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
                                            {item.completed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3 h-3" />}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm text-gray-800">
                                              {item.item}
                                              {item.isRequired && <span className="text-red-500 ml-1">*</span>}
                                              {item.isPhotoRequired && <span className="text-blue-500 ml-1">📷</span>}
                                            </p>
                                            {(item.notes || item.remarks) && (
                                              <div className="flex gap-4 mt-1">
                                                {item.notes && <p className="text-xs text-gray-500"><span className="font-medium">Notes:</span> {item.notes}</p>}
                                                {item.remarks && <p className="text-xs text-gray-500"><span className="font-medium">Remarks:</span> {item.remarks}</p>}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {/* Show photos inline for completed view */}
                                        {(itemPhotos[item.id]?.length > 0) && (
                                          <div className="pl-8 flex gap-2 flex-wrap">
                                            {(itemPhotos[item.id]).map((photo, idx) => (
                                              <img
                                                key={photo.id || idx}
                                                src={photo.url}
                                                alt={photo.fileName || 'Photo'}
                                                className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                                onClick={() => setEnlargedPhoto({ url: photo.url, fileName: photo.fileName })}
                                              />
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      /* Editable in-progress view */
                                      <>
                                        <div className="flex items-start gap-3">
                                          <Checkbox
                                            checked={item.completed}
                                            onCheckedChange={() => handleSvChecklistToggle(item.id)}
                                            disabled={!item.id}
                                          />
                                          <div className="flex-1 min-w-0">
                                            <p className={`font-medium text-sm ${item.completed ? 'line-through text-gray-400' : ''}`}>
                                              {item.item}
                                              {item.isRequired && <span className="text-red-500 ml-1">*</span>}
                                              {item.isConditional && <span className="text-amber-500 ml-1" title={item.conditionNote}>⚡</span>}
                                              {item.isPhotoRequired && <span className="text-blue-500 ml-1" title="Photo required">📷</span>}
                                            </p>
                                            {item.isConditional && item.conditionNote && (
                                              <p className="text-xs text-amber-600 mt-0.5">{item.conditionNote}</p>
                                            )}
                                          </div>
                                        </div>

                                        <div className="flex gap-2 pl-7">
                                          <Input
                                            placeholder="Notes..."
                                            value={item.notes || ''}
                                            onChange={(e) => handleSvChecklistNotes(item.id, e.target.value)}
                                            className="flex-1 h-8 text-sm"
                                            disabled={!item.id}
                                          />
                                          <Input
                                            placeholder="Remarks..."
                                            value={item.remarks || ''}
                                            onChange={(e) => handleSvChecklistRemarks(item.id, e.target.value)}
                                            className="flex-1 h-8 text-sm"
                                            disabled={!item.id}
                                          />
                                        </div>

                                        {item.id && (
                                          <div className="pl-7 flex items-center gap-2 flex-wrap">
                                            {/* Upload button */}
                                            <label className={`flex items-center gap-1 px-2 py-1.5 rounded border text-xs cursor-pointer hover:bg-gray-50 ${item.isPhotoRequired && !(itemPhotos[item.id]?.length) ? 'border-blue-300 text-blue-600 bg-blue-50' : 'text-gray-500'
                                              }`}>
                                              {uploadingPhotoItemId === item.id ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                              ) : (
                                                <Camera className="w-3 h-3" />
                                              )}
                                              Upload Photo
                                              <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file) handlePhotoUpload(project.id, selectedVisitNumber, item.id, file);
                                                  e.target.value = '';
                                                }}
                                                disabled={uploadingPhotoItemId === item.id}
                                              />
                                            </label>
                                            {/* View Photos toggle */}
                                            {(itemPhotos[item.id]?.length > 0) && (
                                              <button
                                                className="flex items-center gap-1 px-2 py-1.5 rounded border text-xs text-gray-600 hover:bg-gray-50"
                                                onClick={() => setViewingPhotosItemId(viewingPhotosItemId === item.id ? null : item.id)}
                                              >
                                                <Image className="w-3 h-3" />
                                                {viewingPhotosItemId === item.id ? 'Hide' : 'View'} Photos ({itemPhotos[item.id].length})
                                                {viewingPhotosItemId === item.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                        {/* Expanded photo gallery */}
                                        {viewingPhotosItemId === item.id && (itemPhotos[item.id] || []).length > 0 && (
                                          <div className="pl-7 mt-2 flex gap-3 flex-wrap">
                                            {(itemPhotos[item.id]).map((photo, idx) => (
                                              <div key={photo.id || idx} className="relative group">
                                                <img
                                                  src={photo.url}
                                                  alt={photo.fileName || 'Photo'}
                                                  className="w-28 h-28 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                                  onClick={() => setEnlargedPhoto({ url: photo.url, fileName: photo.fileName })}
                                                />
                                                <button
                                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                  onClick={() => handleDeletePhoto(project.id, selectedVisitNumber, photo.id)}
                                                  title="Delete photo"
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </button>
                                                <p className="text-[10px] text-gray-400 mt-0.5 truncate w-28">{photo.fileName}</p>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ));
                        })()}

                        {/* Action buttons - fixed to viewport bottom on mobile */}
                        {isEditable && (
                          <>
                            {/* Spacer so last checklist item isn't hidden behind the fixed bar */}
                            <div className="h-20" aria-hidden="true" />
                            <div className="fixed left-0 right-0 bottom-0 z-50 flex justify-end gap-3 px-4 py-3 bg-white border-t shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.15)] pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                              <Button
                                variant="secondary"
                                onClick={() => handleSaveSvProgress(project)}
                                disabled={svSaving}
                                className="flex-1 sm:flex-none"
                              >
                                {svSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Save Progress
                              </Button>
                              <Button
                                onClick={() => handleCompleteSv(project)}
                                disabled={svSaving || svChecklist.some(item => item.isRequired && !item.completed) || svChecklist.some(item => item.isPhotoRequired && !(itemPhotos[item.id]?.length))}
                                className="flex-1 sm:flex-none"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Mark Complete
                              </Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </>
                  );
                })() : (
                  <div className="flex items-center justify-center h-96 text-gray-400">
                    <div className="text-center">
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Select a project to view details</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Work Progress Tab */}
          <TabsContent value="progress">
            {/* Master-Detail Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* LEFT PANEL - Project List */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    Installation Projects ({projects.filter(p => [StageKey.InstallationScheduled, StageKey.InstallationInProgress].includes(p.stageKey) && p.supervisorId === user?.id).length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {projects.filter(p => [StageKey.InstallationScheduled, StageKey.InstallationInProgress].includes(p.stageKey) && p.supervisorId === user?.id).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Wrench className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>No projects in installation stage</p>
                      </div>
                    ) : (
                      projects
                        .filter(p => [StageKey.InstallationScheduled, StageKey.InstallationInProgress].includes(p.stageKey) && p.supervisorId === user?.id)
                        .map(p => {
                          const isSelected = wpSelectedProject?.id === p.id;
                          const pct = wpSelectedProject?.id === p.id && wpChecklist
                            ? (wpChecklist.totalItems > 0 ? Math.round((wpChecklist.completedItems / wpChecklist.totalItems) * 100) : 0)
                            : null;

                          return (
                            <div
                              key={p.id}
                              onClick={() => {
                                setWpSelectedProject(p);
                                fetchInstallationChecklist(p.id);
                              }}
                              className={`p-3 rounded-lg cursor-pointer transition-all ${isSelected
                                ? 'bg-blue-50 border-2 border-blue-500'
                                : 'bg-white border border-gray-200 hover:border-blue-300'
                                }`}
                            >
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900 truncate">{p.client_name}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.stageKey === StageKey.InstallationInProgress
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-amber-100 text-amber-700'
                                  }`}>
                                  {p.stageKey === StageKey.InstallationInProgress ? 'In Progress' : 'Scheduled'}
                                </span>
                              </div>
                              {pct !== null && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                    <span>{wpChecklist?.completedItems}/{wpChecklist?.totalItems} items</span>
                                    <span>{pct}%</span>
                                  </div>
                                  <Progress value={pct} className="h-1.5" />
                                </div>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RIGHT PANEL - Installation Checklist Details */}
              <Card className="lg:col-span-2">
                {wpSelectedProject ? (
                  <>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-xl">{wpSelectedProject.client_name}</CardTitle>
                          <p className="text-sm text-gray-500 flex items-start gap-1 mt-1">
                            <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="break-words">{wpSelectedProject.location}</span>
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${wpSelectedProject.stageKey === StageKey.InstallationInProgress
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                          }`}>
                          {wpSelectedProject.status}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
                      {/* Loading */}
                      {wpLoading && (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                          <span className="text-gray-500">Loading checklist...</span>
                        </div>
                      )}

                      {/* Checklist content */}
                      {!wpLoading && wpChecklist && (
                        <>
                          {/* Progress summary */}
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              {wpChecklist.completedItems}/{wpChecklist.totalItems} completed
                            </span>
                            {wpChecklist.pendingApproval > 0 && (
                              <span className="text-amber-600 text-xs font-medium">
                                {wpChecklist.pendingApproval} pending approval
                              </span>
                            )}
                          </div>
                          <Progress
                            value={wpChecklist.totalItems > 0 ? Math.round((wpChecklist.completedItems / wpChecklist.totalItems) * 100) : 0}
                            className="h-2"
                          />

                          {/* Search */}
                          <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              placeholder="Search checklist items..."
                              value={wpSearch}
                              onChange={(e) => setWpSearch(e.target.value)}
                              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            {wpSearch && (
                              <button
                                type="button"
                                onClick={() => setWpSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Entities → Units → Groups → Items */}
                          {(() => {
                            const _wpSearchTerm = wpSearch.trim().toLowerCase();
                            const _wpFilteredEntities = _wpSearchTerm
                              ? (wpChecklist.entities || []).map(entity => ({
                                ...entity,
                                units: (entity.units || []).map(unit => ({
                                  ...unit,
                                  groups: (unit.groups || [])
                                    .map(g => ({ ...g, items: (g.items || []).filter(it => (it.item || it.checklistItemName || '').toLowerCase().includes(_wpSearchTerm)) }))
                                    .filter(g => g.items.length > 0)
                                })).filter(unit => (unit.groups || []).length > 0)
                              })).filter(entity => (entity.units || []).length > 0)
                              : (wpChecklist.entities || []);

                            if (_wpSearchTerm && _wpFilteredEntities.length === 0) {
                              return (
                                <div className="text-center py-8 text-sm text-gray-500">
                                  No checklist items match "{wpSearch}"
                                </div>
                              );
                            }

                            return _wpFilteredEntities.map(entity => (
                              <div key={entity.entityId} className="space-y-2">
                                {/* Entity header */}
                                <button
                                  onClick={() => setWpExpandedEntities(prev => ({ ...prev, [entity.entityId]: !prev[entity.entityId] }))}
                                  className="w-full flex items-center justify-between px-3 py-1.5 bg-gray-100 rounded"
                                >
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-gray-600" />
                                    <span className="font-semibold text-xs text-gray-600">{entity.entityName}</span>
                                    <span className="text-muted-foreground text-xs">
                                      ({entity.completedItems}/{entity.totalItems})
                                    </span>
                                    {entity.pendingApproval > 0 && (
                                      <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 font-medium">
                                        {entity.pendingApproval} pending
                                      </span>
                                    )}
                                  </div>
                                  {wpExpandedEntities[entity.entityId] || _wpSearchTerm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>

                                {/* Entity content - Units */}
                                {(wpExpandedEntities[entity.entityId] || _wpSearchTerm) && (
                                  <div className="pl-2 space-y-2">
                                    {(entity.units || []).map(unit => (
                                      <div key={unit.unitId} className="border rounded-md overflow-hidden">
                                        {/* Unit header */}
                                        <button
                                          onClick={() => setWpExpandedUnits(prev => ({ ...prev, [unit.unitId]: !prev[unit.unitId] }))}
                                          className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm">{unit.unitName}</span>
                                            <span className="text-xs text-gray-500">
                                              ({unit.completedItems}/{unit.totalItems})
                                            </span>
                                            {unit.pendingApproval > 0 && (
                                              <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                                                {unit.pendingApproval} pending
                                              </span>
                                            )}
                                          </div>
                                          {wpExpandedUnits[unit.unitId] || _wpSearchTerm ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                        </button>

                                        {/* Unit checklist items */}
                                        {(wpExpandedUnits[unit.unitId] || _wpSearchTerm) && (
                                          <div className="p-2 space-y-3">
                                            {(unit.groups || []).map(group => (
                                              <div key={group.groupName}>
                                                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 px-1">
                                                  {group.groupName}
                                                  <span className="ml-2 text-muted-foreground normal-case">
                                                    ({(group.items || []).filter(i => i.isCompleted).length}/{(group.items || []).length})
                                                  </span>
                                                </h5>
                                                <div className="space-y-2">
                                                  {(group.items || []).map(item => (
                                                    <div key={item.id} className="p-3 border rounded-lg space-y-2">
                                                      <div className="flex items-start gap-3">
                                                        {/* Status icon */}
                                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.isApproved
                                                          ? 'bg-green-100 text-green-600'
                                                          : item.isCompleted
                                                            ? 'bg-amber-100 text-amber-500'
                                                            : 'bg-gray-100 text-gray-400'
                                                          }`}>
                                                          {item.isApproved ? (
                                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                                          ) : item.isCompleted ? (
                                                            <Clock className="w-3.5 h-3.5" />
                                                          ) : (
                                                            <div className="w-2.5 h-2.5 rounded-full border-2 border-gray-300" />
                                                          )}
                                                        </div>

                                                        {/* Item name & details */}
                                                        <div className="flex-1 min-w-0">
                                                          <p className="font-medium text-sm text-gray-800">
                                                            {item.item || item.itemName}
                                                            {item.requiresSupervisorApproval && (
                                                              <span className="text-amber-500 ml-1" title="Requires supervisor approval">⚑</span>
                                                            )}
                                                          </p>
                                                          {item.completedBy && (
                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                              Completed by {item.completedBy}
                                                              {item.completedAt && ` · ${format(new Date(item.completedAt), 'dd MMM, h:mm a')}`}
                                                            </p>
                                                          )}
                                                          {item.approvedBy && (
                                                            <p className="text-xs text-green-600 mt-0.5">
                                                              ✓ Approved by {item.approvedBy}
                                                              {item.approvedAt && ` · ${format(new Date(item.approvedAt), 'dd MMM, h:mm a')}`}
                                                            </p>
                                                          )}
                                                          {item.notes && (
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                              <span className="font-medium">Notes:</span> {item.notes}
                                                            </p>
                                                          )}
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                          {/* Photos button */}
                                                          {item.photos?.length > 0 && (
                                                            <button
                                                              onClick={() => loadItemPhotos(wpSelectedProject.id, item.id, item.photos)}
                                                              className="flex items-center gap-1 px-2 py-1.5 rounded border text-xs text-gray-600 hover:bg-gray-50"
                                                              disabled={wpLoadingPhotos === item.id}
                                                            >
                                                              {wpLoadingPhotos === item.id ? (
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                              ) : (
                                                                <Image className="w-3 h-3" />
                                                              )}
                                                              {wpPhotosOpen === item.id ? 'Hide' : 'View'} Photos ({item.photos.length})
                                                              {wpPhotosOpen === item.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                            </button>
                                                          )}

                                                          {/* Approve button */}
                                                          {item.requiresSupervisorApproval && item.isCompleted && !item.isSupervisorApproved && (
                                                            <Button
                                                              size="sm"
                                                              className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                                              onClick={() => handleApproveItem(wpSelectedProject.id, item.id)}
                                                              disabled={wpApprovingItem === item.id}
                                                            >
                                                              {wpApprovingItem === item.id ? (
                                                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                                              ) : (
                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                              )}
                                                              Approve
                                                            </Button>
                                                          )}
                                                        </div>
                                                      </div>

                                                      {/* Inline photo gallery */}
                                                      {wpPhotosOpen === item.id && wpPhotoUrls[item.id] && (
                                                        <div className="pl-8 flex gap-3 flex-wrap">
                                                          {wpPhotoUrls[item.id].map((photo, idx) => (
                                                            <div key={idx} className="relative group">
                                                              <img
                                                                src={photo.url}
                                                                alt={photo.fileName}
                                                                className="w-28 h-28 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                                                onClick={() => setWpEnlargedPhoto(photo)}
                                                              />
                                                              <p className="text-[10px] text-gray-400 mt-0.5 truncate w-28">{photo.fileName}</p>
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ));
                          })()}

                          {(wpChecklist.entities || []).length === 0 && (
                            <div className="text-center text-gray-500 py-8">
                              No checklist items found for this project
                            </div>
                          )}
                        </>
                      )}

                      {!wpLoading && !wpChecklist && (
                        <div className="text-center text-gray-400 py-4">
                          Select a project to view checklist
                        </div>
                      )}
                    </CardContent>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-96 text-gray-400">
                    <div className="text-center">
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Select a project to view installation progress</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Enlarged Photo Dialog */}
            <Dialog open={!!wpEnlargedPhoto} onOpenChange={(open) => !open && setWpEnlargedPhoto(null)}>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{wpEnlargedPhoto?.fileName || 'Photo'}</DialogTitle>
                </DialogHeader>
                {wpEnlargedPhoto && (
                  <img src={wpEnlargedPhoto.url} alt={wpEnlargedPhoto.fileName} className="w-full rounded-lg" />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Client Review Tab */}
          <TabsContent value="clientreview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* LEFT PANEL - Project List */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    Client Review ({projects.filter(p => p.stageKey === StageKey.ClientReview && p.supervisorId === user?.id).length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {projects.filter(p => p.stageKey === StageKey.ClientReview && p.supervisorId === user?.id).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <ClipboardCheck className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>No projects in client review</p>
                      </div>
                    ) : (
                      projects
                        .filter(p => p.stageKey === StageKey.ClientReview && p.supervisorId === user?.id)
                        .map(p => {
                          const isSelected = crSelectedProject?.id === p.id;
                          const pct = isSelected && crChecklist
                            ? (crChecklist.totalItems > 0 ? Math.round((crChecklist.completedItems / crChecklist.totalItems) * 100) : 0)
                            : null;

                          return (
                            <div
                              key={p.id}
                              onClick={() => {
                                setCrSelectedProject(p);
                                fetchClientReviewChecklist(p.id);
                              }}
                              className={`p-3 rounded-lg cursor-pointer transition-all ${isSelected
                                ? 'bg-cyan-50 border-2 border-cyan-500'
                                : 'bg-white border border-gray-200 hover:border-cyan-300'
                                }`}
                            >
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900 truncate">{p.client_name}</h3>
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">
                                  Client Review
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 truncate">{p.location}</p>
                              {pct !== null && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                    <span>{crChecklist?.completedItems}/{crChecklist?.totalItems} items</span>
                                    <span>{pct}%</span>
                                  </div>
                                  <Progress value={pct} className="h-1.5" />
                                </div>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RIGHT PANEL - Client Review Checklist */}
              <Card className="lg:col-span-2">
                {crSelectedProject ? (
                  <>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-xl">{crSelectedProject.client_name}</CardTitle>
                          <p className="text-sm text-gray-500 flex items-start gap-1 mt-1">
                            <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="break-words">{crSelectedProject.location}</span>
                          </p>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 bg-cyan-100 text-cyan-700">
                          Client Review
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
                      {crLoading && (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-cyan-600 mr-2" />
                          <span className="text-gray-500">Loading checklist...</span>
                        </div>
                      )}

                      {!crLoading && crChecklist && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              {crChecklist.completedItems}/{crChecklist.totalItems} completed
                            </span>
                            {crChecklist.pendingApproval > 0 && (
                              <span className="text-amber-600 text-xs font-medium">
                                {crChecklist.pendingApproval} pending approval
                              </span>
                            )}
                          </div>
                          <Progress
                            value={crChecklist.totalItems > 0 ? Math.round((crChecklist.completedItems / crChecklist.totalItems) * 100) : 0}
                            className="h-2"
                          />

                          {/* Groups → Items (flat, no entity/unit hierarchy) */}
                          {(crChecklist.groups || []).map(group => (
                            <div key={group.groupName}>
                              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 px-1">
                                {group.groupName}
                                <span className="ml-2 text-muted-foreground normal-case">
                                  ({(group.items || []).filter(i => i.isApproved).length}/{(group.items || []).length})
                                </span>
                              </h5>
                              <div className="space-y-2">
                                {(group.items || []).map(item => (
                                  <div key={item.id} className={`p-3 border rounded-lg space-y-2 ${item.isApproved ? 'bg-green-50 border-green-200' : item.isCompleted ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-start gap-3">
                                      {/* Status icon */}
                                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.isApproved
                                        ? 'bg-green-100 text-green-600'
                                        : item.isCompleted
                                          ? 'bg-amber-100 text-amber-500'
                                          : 'bg-gray-100 text-gray-400'
                                        }`}>
                                        {item.isApproved ? (
                                          <CheckCircle2 className="w-3.5 h-3.5" />
                                        ) : item.isCompleted ? (
                                          <Clock className="w-3.5 h-3.5" />
                                        ) : (
                                          <Circle className="w-2.5 h-2.5" />
                                        )}
                                      </div>

                                      {/* Item details */}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <p className="font-medium text-sm text-gray-800">
                                            {item.itemName}
                                          </p>
                                          {item.isRequired && <span className="text-red-500 text-xs">*</span>}
                                          {item.isPhotoRequired && (
                                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">
                                              <Camera className="w-2.5 h-2.5" /> Photo
                                            </span>
                                          )}
                                          {item.requiresSupervisorApproval && (
                                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-orange-50 text-orange-600 border border-orange-200">
                                              <ShieldAlert className="w-2.5 h-2.5" /> Supervisor
                                            </span>
                                          )}
                                        </div>

                                        {/* Approval status */}
                                        {item.isCompleted && item.requiresSupervisorApproval && (
                                          <div className="mt-1">
                                            {item.isSupervisorApproved === true ? (
                                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                                <ShieldCheck className="w-3 h-3" /> Supervisor Approved
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                                                <ShieldAlert className="w-3 h-3" /> Pending Supervisor Approval
                                              </span>
                                            )}
                                          </div>
                                        )}

                                        {item.completedBy && (
                                          <p className="text-xs text-gray-400 mt-0.5">
                                            Completed by {item.completedBy}
                                            {item.completedAt && ` · ${format(new Date(item.completedAt), 'dd MMM, h:mm a')}`}
                                          </p>
                                        )}
                                        {item.approvedBy && (
                                          <p className="text-xs text-green-600 mt-0.5">
                                            ✓ Approved by {item.approvedBy}
                                            {item.approvedAt && ` · ${format(new Date(item.approvedAt), 'dd MMM, h:mm a')}`}
                                          </p>
                                        )}
                                        {item.notes && (
                                          <p className="text-xs text-gray-500 mt-0.5">
                                            <span className="font-medium">Notes:</span> {item.notes}
                                          </p>
                                        )}

                                        {/* Photos view */}
                                        {item.photoCount > 0 && (
                                          <div className="mt-1.5">
                                            <button
                                              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                                              onClick={() => loadCrItemPhotos(crSelectedProject.id, item.id)}
                                            >
                                              <Image className="w-3 h-3" />
                                              {crPhotosExpanded[item.id] ? 'Hide' : 'View'} Photos ({item.photoCount})
                                            </button>
                                          </div>
                                        )}

                                        {/* Photo thumbnails */}
                                        {crPhotosExpanded[item.id] && crItemPhotos[item.id] && (
                                          <div className="mt-2 flex flex-wrap gap-2">
                                            {crItemPhotos[item.id].map((photo) => (
                                              <div key={photo.id} className="relative group">
                                                <img
                                                  src={photo.url}
                                                  alt={photo.fileName}
                                                  className="w-16 h-16 object-cover rounded border cursor-pointer hover:opacity-80"
                                                  onClick={() => setCrEnlargedPhoto(photo)}
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Actions */}
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {/* Approve button - for items completed by aligner needing approval */}
                                        {item.requiresSupervisorApproval && item.isCompleted && !item.isSupervisorApproved && (
                                          <Button
                                            size="sm"
                                            className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                            onClick={() => handleCrApproveItem(crSelectedProject.id, item.id)}
                                            disabled={crApprovingItem === item.id}
                                          >
                                            {crApprovingItem === item.id ? (
                                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                            ) : (
                                              <CheckCircle2 className="w-3 h-3 mr-1" />
                                            )}
                                            Approve
                                          </Button>
                                        )}

                                        {/* Supervisor direct toggle - for items not yet completed */}
                                        {!item.isCompleted && !item.isApproved && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 text-xs"
                                            onClick={() => handleCrSupervisorUpdate(crSelectedProject.id, item.id, item.isCompleted)}
                                            disabled={crUpdatingItem === item.id}
                                          >
                                            {crUpdatingItem === item.id ? (
                                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                            ) : (
                                              <CheckCircle2 className="w-3 h-3 mr-1" />
                                            )}
                                            Mark Done
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}

                          {(crChecklist.groups || []).length === 0 && (
                            <div className="text-center text-gray-500 py-8">
                              No checklist items found
                            </div>
                          )}

                          {/* Complete Client Review button */}
                          <div className="flex gap-3 mt-4 pt-4 border-t">
                            <Button
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleCrComplete(crSelectedProject.id)}
                              disabled={crCompleting || !crChecklist || crChecklist.completedItems < crChecklist.totalItems}
                            >
                              {crCompleting ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                              )}
                              Complete Client Review
                            </Button>
                          </div>
                        </>
                      )}

                      {!crLoading && !crChecklist && (
                        <div className="text-center text-gray-400 py-4">
                          Select a project to view checklist
                        </div>
                      )}
                    </CardContent>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-96 text-gray-400">
                    <div className="text-center">
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Select a project to view client review progress</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Aligners Tab */}
          <TabsContent value="installers">
            <Card>
              <CardHeader>
                <CardTitle>Aligners & Teams</CardTitle>
                <CardDescription>Teams and members assigned to your installation-in-progress projects</CardDescription>
              </CardHeader>
              <CardContent>
                {installers.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No teams assigned to your in-progress projects</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {installers.map(team => (
                      <div key={team.teamId} className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
                              <Users className="w-4 h-4 text-teal-700" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{team.teamName}</h4>
                              <p className="text-xs text-gray-500">Skill: {team.skillLevel} · {team.members.length} member{team.members.length !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {team.projects.map(p => (
                              <span key={p.projectId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {p.clientName}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <Table className="min-w-[500px]">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="whitespace-nowrap">Name</TableHead>
                                <TableHead className="whitespace-nowrap">Email</TableHead>
                                <TableHead className="whitespace-nowrap">Phone</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {team.members.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={3} className="text-center text-gray-400 py-4">No active members</TableCell>
                                </TableRow>
                              ) : (
                                team.members.map(m => (
                                  <TableRow key={m.userId}>
                                    <TableCell className="font-medium">{m.name}</TableCell>
                                    <TableCell className="text-gray-600">{m.email}</TableCell>
                                    <TableCell>
                                      {m.phone ? (
                                        <span className="flex items-center gap-1 text-gray-600">
                                          <Phone className="w-3 h-3" />
                                          {m.phone}
                                        </span>
                                      ) : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle>My Schedule</CardTitle>
                <CardDescription>Site visit bookings and installation project timelines</CardDescription>
              </CardHeader>
              <CardContent>
                <SupervisorCalendar />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Aligner Dialog */}
      <Dialog open={addInstallerDialogOpen} onOpenChange={setAddInstallerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Aligner</DialogTitle>
            <DialogDescription>Create a new aligner profile</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={newInstaller.name}
                onChange={(e) => setNewInstaller({ ...newInstaller, name: e.target.value })}
                placeholder="Enter aligner name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newInstaller.email}
                onChange={(e) => setNewInstaller({ ...newInstaller, email: e.target.value })}
                placeholder="aligner@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={newInstaller.password}
                onChange={(e) => setNewInstaller({ ...newInstaller, password: e.target.value })}
                placeholder="Create password"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={newInstaller.phone}
                onChange={(e) => setNewInstaller({ ...newInstaller, phone: e.target.value })}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
            <div className="space-y-2">
              <Label>Team (Optional)</Label>
              <Input
                value={newInstaller.team}
                onChange={(e) => setNewInstaller({ ...newInstaller, team: e.target.value })}
                placeholder="Team A"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setAddInstallerDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateInstaller}
              data-testid="create-installer-btn"
              disabled={creatingInstaller}
            >
              {creatingInstaller ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Installer'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Detail Dialog */}
      <Dialog open={jobDetailDialogOpen} onOpenChange={setJobDetailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedJob?.client_name}</DialogTitle>
            <DialogDescription>{selectedJob?.location}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Sq.Ft</p>
                <p className="font-medium">{selectedJob?.sqft}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Installer</p>
                <p className="font-medium">{selectedJob?.installer_name || 'Not assigned'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Progress</p>
                <p className="font-medium">{selectedJob?.progress_percent}%</p>
              </div>
            </div>

            {selectedJob?.checklist && (
              <div className="space-y-2">
                <p className="font-medium">Checklist</p>
                <div className="space-y-1">
                  {selectedJob.checklist.map(item => (
                    <div key={item.id} className="flex items-center gap-2 text-sm">
                      {item.completed ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 border rounded" />
                      )}
                      <span className={item.completed ? 'text-gray-400' : ''}>{item.item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedJob?.progress_percent === 100 && selectedJob?.status === 'In Progress' && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleApproveCompletion(selectedJob.id, false)}
                >
                  Reject
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => handleApproveCompletion(selectedJob.id, true)}
                  data-testid="approve-completion-btn"
                >
                  Approve Completion
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Dialog */}
      <Dialog open={!!enlargedPhoto} onOpenChange={(open) => { if (!open) setEnlargedPhoto(null); }}>
        <DialogContent className="max-w-[90vw] sm:max-w-3xl p-2">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-sm truncate">{enlargedPhoto?.fileName}</DialogTitle>
          </DialogHeader>
          {enlargedPhoto && (
            <div className="flex items-center justify-center">
              <img
                src={enlargedPhoto.url}
                alt={enlargedPhoto.fileName || 'Photo'}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={!!editingAddressProjectId} onOpenChange={(open) => { if (!open) setEditingAddressProjectId(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Location</DialogTitle>
            <DialogDescription>
              Search for an address, click on the map, or use your live location
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id={`sv-search-${editingAddressProjectId}`}
                type="text"
                placeholder="Search address..."
                className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
                value={editingSiteAddress}
                onChange={(e) => setEditingSiteAddress(e.target.value)}
              />
            </div>
            <div id={`sv-map-${editingAddressProjectId}`} className="w-full h-72 rounded-lg border" />
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {editingLatitude && <span>Lat: {editingLatitude}</span>}
              {editingLongitude && <span>Lng: {editingLongitude}</span>}
            </div>
            {editingSiteAddress && (
              <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                <MapPin className="w-3 h-3 inline mr-1" />
                {editingSiteAddress}
              </p>
            )}
          </div>
          <div className="flex justify-between">
            <Button size="sm" variant="outline" onClick={handleGetLiveLocation}>
              <MapPin className="w-4 h-4 mr-1" />
              Live Location
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingAddressProjectId(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleSaveAddress(editingAddressProjectId)} disabled={savingAddress || !editingSiteAddress}>
                {savingAddress ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Save Address
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enlarged Photo Dialog for Client Review */}
      <Dialog open={!!crEnlargedPhoto} onOpenChange={() => setCrEnlargedPhoto(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl p-2">
          <DialogHeader>
            <DialogTitle className="text-sm">{crEnlargedPhoto?.fileName}</DialogTitle>
          </DialogHeader>
          {crEnlargedPhoto && (
            <img src={crEnlargedPhoto.url} alt={crEnlargedPhoto.fileName} className="w-full max-h-[70vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>

    </div >
  );
};

export default SupervisorDashboard;
